import React, { useState, useEffect, useCallback } from 'react';
import { API } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { LogIn, Clock, Users as UsersIcon, Activity, ArrowUpRight, Search, Globe2, ChevronRight } from 'lucide-react';

/* ---------------- helpers ---------------- */
const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const formatGeo = (geo) => {
  if (!geo) return null;
  const parts = [];
  if (geo.city) parts.push(geo.city);
  if (geo.region && geo.region !== geo.city) parts.push(geo.region);
  if (geo.country) parts.push(geo.country);
  return parts.join(', ');
};

const countryFlag = (code) => {
  if (!code || code.length !== 2) return '';
  try {
    return String.fromCodePoint(...[...code.toUpperCase()].map(c => 127397 + c.charCodeAt(0)));
  } catch { return ''; }
};

const relativeTime = (iso) => {
  if (!iso) return '';
  const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return '—';
  const s = Math.max(0, parseInt(seconds, 10));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

const ROLE_COLORS = {
  super_admin: 'bg-purple-100 text-purple-700 border-purple-200',
  client_admin: 'bg-blue-100 text-blue-700 border-blue-200',
  team_leader: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  recruiter: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  candidate: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

/* ---------------- component ---------------- */
const LoginActivityCard = ({ token, defaultLimit = 8 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [days, setDays] = useState(30);
  const [role, setRole] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);

  const openSessionDetail = async (session) => {
    setSelectedSession(session);
    setSessionDetail(null);
    setSessionLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(
        `${API}/admin/analytics/logins/${session.session_id}/pages`,
        { headers, withCredentials: true },
      );
      setSessionDetail(res.data);
    } catch { /* silent */ }
    setSessionLoading(false);
  };

  const load = useCallback(async (opts = {}) => {
    setLoading(true);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const params = {
      days: opts.days ?? days,
      limit: opts.limit ?? (expanded ? 200 : defaultLimit),
      role: opts.role ?? role,
      search: opts.search ?? search,
    };
    try {
      const res = await axios.get(`${API}/admin/analytics/logins`, { headers, params, withCredentials: true });
      setData(res.data);
    } catch { /* silent */ }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, days, role, search, expanded, defaultLimit]);

  useEffect(() => { load(); /* initial */ /* eslint-disable-next-line */ }, [token]);
  useEffect(() => { if (expanded) load(); /* eslint-disable-next-line */ }, [expanded, days, role]);

  const sessions = data?.sessions || [];
  const summary = data?.summary || {};

  const renderRow = (s) => (
    <button
      type="button"
      key={s.session_id}
      onClick={() => openSessionDetail(s)}
      className="w-full flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0 hover:bg-slate-50 -mx-1 px-1 rounded transition-colors text-left"
      data-testid={`login-row-${s.session_id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-medium text-slate-900 text-sm truncate">{s.name || s.email}</p>
          {s.is_active && (
            <Badge className="bg-green-100 text-green-700 border-green-200 text-[9px] px-1.5 py-0 h-4 gap-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> Active
            </Badge>
          )}
          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-4 border ${ROLE_COLORS[s.role] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
            {s.role}
          </Badge>
        </div>
        <p className="text-xs text-slate-500 truncate">{s.email}</p>
        {s.geo && (
          <p className="text-[11px] text-slate-500 truncate flex items-center gap-1">
            <span>{countryFlag(s.geo.country_code)}</span>
            <Globe2 className="w-2.5 h-2.5" />
            <span>{formatGeo(s.geo)}</span>
          </p>
        )}
      </div>
      <div className="text-right flex-shrink-0">
        <p className="text-xs font-medium text-slate-700" title={formatDateTime(s.login_at)}>
          {formatDateTime(s.login_at)}
        </p>
        <p className="text-[11px] text-slate-500">
          <Clock className="w-2.5 h-2.5 inline mr-0.5" />
          {formatDuration(s.duration_seconds ?? s.duration_seconds_live)}
          {s.is_active ? ' · ongoing' : ''}
        </p>
        {s.page_views_count > 0 && (
          <p className="text-[10px] text-indigo-600 mt-0.5">{s.page_views_count} pages</p>
        )}
      </div>
      <ChevronRight className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
    </button>
  );

  return (
    <>
      <Card data-testid="login-activity-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                <LogIn className="w-4 h-4 text-indigo-600" />
              </div>
              <CardTitle style={{ fontFamily: 'Outfit' }}>Login Activity</CardTitle>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(true)}
              data-testid="login-activity-view-all"
            >
              View All <ArrowUpRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {/* Summary strip */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="bg-slate-50 rounded-lg p-2 text-center">
              <p className="text-lg font-bold text-slate-900">{summary.total_logins ?? '—'}</p>
              <p className="text-[10px] uppercase tracking-wide text-slate-500">Logins (30d)</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-2 text-center">
              <p className="text-lg font-bold text-slate-900">{summary.unique_users ?? '—'}</p>
              <p className="text-[10px] uppercase tracking-wide text-slate-500">Unique Users</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-2 text-center">
              <p className="text-lg font-bold text-slate-900">{formatDuration(summary.avg_duration_seconds)}</p>
              <p className="text-[10px] uppercase tracking-wide text-slate-500">Avg Session</p>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-6">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600" />
            </div>
          ) : sessions.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">No logins recorded yet</p>
          ) : (
            <div className="space-y-0">
              {sessions.slice(0, defaultLimit).map(renderRow)}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Expanded modal */}
      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="login-activity-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Activity className="w-5 h-5 text-indigo-600" />
              Login Activity
            </DialogTitle>
          </DialogHeader>

          {/* Summary cards */}
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <LogIn className="w-4 h-4 text-indigo-600" />
                <p className="text-[10px] uppercase tracking-wide text-indigo-700 font-semibold">Total Logins</p>
              </div>
              <p className="text-2xl font-bold text-indigo-900">{summary.total_logins ?? 0}</p>
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <UsersIcon className="w-4 h-4 text-blue-600" />
                <p className="text-[10px] uppercase tracking-wide text-blue-700 font-semibold">Unique Users</p>
              </div>
              <p className="text-2xl font-bold text-blue-900">{summary.unique_users ?? 0}</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-emerald-600" />
                <p className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">Avg Duration</p>
              </div>
              <p className="text-2xl font-bold text-emerald-900">{formatDuration(summary.avg_duration_seconds)}</p>
              <p className="text-[10px] text-emerald-700 mt-0.5">from {summary.closed_sessions || 0} closed sessions</p>
            </div>
            <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Activity className="w-4 h-4 text-amber-600" />
                <p className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold">Active Now</p>
              </div>
              <p className="text-2xl font-bold text-amber-900">{summary.active_now ?? 0}</p>
              <p className="text-[10px] text-amber-700 mt-0.5">activity in last 15min</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap py-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search name or email..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && load({})}
                className="pl-8"
                data-testid="login-search"
              />
            </div>
            <Select value={role} onValueChange={(v) => { setRole(v); }}>
              <SelectTrigger className="w-[160px]" data-testid="login-role-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                <SelectItem value="super_admin">Super admin</SelectItem>
                <SelectItem value="client_admin">Client admin</SelectItem>
                <SelectItem value="team_leader">Team leader</SelectItem>
                <SelectItem value="recruiter">Recruiter</SelectItem>
                <SelectItem value="candidate">Candidate</SelectItem>
              </SelectContent>
            </Select>
            <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
              <SelectTrigger className="w-[140px]" data-testid="login-days-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Last 24 hours</SelectItem>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Last year</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => load({})} data-testid="login-refresh">
              Refresh
            </Button>
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto border rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr className="text-left">
                  <th className="px-3 py-2 font-medium text-slate-600 text-xs uppercase">User</th>
                  <th className="px-3 py-2 font-medium text-slate-600 text-xs uppercase">Role</th>
                  <th className="px-3 py-2 font-medium text-slate-600 text-xs uppercase">Login Date &amp; Time</th>
                  <th className="px-3 py-2 font-medium text-slate-600 text-xs uppercase">Location</th>
                  <th className="px-3 py-2 font-medium text-slate-600 text-xs uppercase">Duration</th>
                  <th className="px-3 py-2 font-medium text-slate-600 text-xs uppercase">Pages</th>
                  <th className="px-3 py-2 font-medium text-slate-600 text-xs uppercase">Method</th>
                  <th className="px-3 py-2 font-medium text-slate-600 text-xs uppercase">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="8" className="text-center py-8"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600 inline-block" /></td></tr>
                ) : sessions.length === 0 ? (
                  <tr><td colSpan="8" className="text-center py-8 text-slate-500">No logins found for the selected filters</td></tr>
                ) : sessions.map(s => (
                  <tr
                    key={s.session_id}
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => openSessionDetail(s)}
                    data-testid={`login-tr-${s.session_id}`}
                  >
                    <td className="px-3 py-2">
                      <p className="font-medium text-slate-900">{s.name || '—'}</p>
                      <p className="text-xs text-slate-500">{s.email}</p>
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className={`text-[10px] border ${ROLE_COLORS[s.role] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                        {s.role}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      <p>{formatDateTime(s.login_at)}</p>
                      <p className="text-[11px] text-slate-500">{relativeTime(s.login_at)}</p>
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      {s.geo ? (
                        <div className="flex items-center gap-1.5 text-xs">
                          <span className="text-base leading-none">{countryFlag(s.geo.country_code)}</span>
                          <div>
                            <p className="font-medium text-slate-800">{s.geo.country || '—'}</p>
                            <p className="text-[11px] text-slate-500">{s.geo.city || s.geo.region || ''}</p>
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">
                          {s.ip ? <span title={s.ip}>Unresolved</span> : '—'}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      {formatDuration(s.duration_seconds ?? s.duration_seconds_live)}
                      {s.is_active && <span className="text-[10px] text-green-600 ml-1">(ongoing)</span>}
                    </td>
                    <td className="px-3 py-2 text-slate-700 text-center">
                      {s.page_views_count || 0}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {s.method === 'google_oauth' ? 'Google' : 'Password'}
                    </td>
                    <td className="px-3 py-2">
                      {s.is_active ? (
                        <Badge className="bg-green-100 text-green-700 text-[10px]">Active</Badge>
                      ) : s.logout_at ? (
                        <Badge variant="outline" className="text-[10px]">Logged out</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] text-slate-500">Ended</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Session Detail Drill-down */}
      <Dialog open={!!selectedSession} onOpenChange={(o) => !o && setSelectedSession(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="session-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Activity className="w-5 h-5 text-indigo-600" />
              Session Details
            </DialogTitle>
          </DialogHeader>

          {selectedSession && (
            <div className="space-y-4 overflow-y-auto">
              {/* Session summary */}
              <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 rounded-lg border">
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">User</p>
                  <p className="text-sm font-medium text-slate-900">{selectedSession.name || selectedSession.email}</p>
                  <p className="text-xs text-slate-500">{selectedSession.email}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Role &amp; Method</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className={`text-[10px] border ${ROLE_COLORS[selectedSession.role] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                      {selectedSession.role}
                    </Badge>
                    <span className="text-xs text-slate-500">via {selectedSession.method === 'google_oauth' ? 'Google' : 'Password'}</span>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Login Time</p>
                  <p className="text-sm text-slate-800">{formatDateTime(selectedSession.login_at)}</p>
                  {selectedSession.logout_at && (
                    <p className="text-xs text-slate-500">Ended {formatDateTime(selectedSession.logout_at)}</p>
                  )}
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Duration</p>
                  <p className="text-sm text-slate-800">
                    {formatDuration(selectedSession.duration_seconds ?? selectedSession.duration_seconds_live)}
                    {selectedSession.is_active && <span className="text-xs text-green-600 ml-1">· active</span>}
                  </p>
                </div>
                {selectedSession.geo && (
                  <div className="col-span-2">
                    <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Location</p>
                    <p className="text-sm text-slate-800 flex items-center gap-1">
                      <span className="text-lg leading-none">{countryFlag(selectedSession.geo.country_code)}</span>
                      {formatGeo(selectedSession.geo)}
                      {selectedSession.ip && <span className="text-xs text-slate-400 ml-2">· IP {selectedSession.ip}</span>}
                    </p>
                    {selectedSession.geo.org && (
                      <p className="text-xs text-slate-500">{selectedSession.geo.org}</p>
                    )}
                  </div>
                )}
                {selectedSession.user_agent && (
                  <div className="col-span-2">
                    <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Device</p>
                    <p className="text-xs text-slate-600 truncate" title={selectedSession.user_agent}>{selectedSession.user_agent}</p>
                  </div>
                )}
              </div>

              {/* Pages timeline */}
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">
                  Pages visited ({sessionDetail?.total_pages || 0})
                </p>
                {sessionLoading ? (
                  <div className="flex justify-center py-6">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600" />
                  </div>
                ) : !sessionDetail?.pages?.length ? (
                  <p className="text-sm text-slate-500 text-center py-6 bg-slate-50 rounded-lg">
                    No pages tracked for this session yet.
                    <br />
                    <span className="text-xs">Page tracking only works for authenticated users on pages loaded after tracking was enabled.</span>
                  </p>
                ) : (
                  <div className="space-y-1 border rounded-lg p-2 max-h-[300px] overflow-y-auto" data-testid="pages-timeline">
                    {sessionDetail.pages.map((p, i) => (
                      <div key={p.view_id} className="flex items-start gap-2 py-1.5 px-2 hover:bg-slate-50 rounded text-xs">
                        <span className="text-slate-400 tabular-nums w-5 text-right flex-shrink-0">{i + 1}.</span>
                        <div className="flex-1 min-w-0">
                          <p className="font-mono text-slate-800 truncate" title={p.path}>{p.path}</p>
                          {p.title && <p className="text-[11px] text-slate-500 truncate">{p.title}</p>}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-slate-700">{formatDateTime(p.viewed_at)}</p>
                          {p.seconds_since_prev > 0 && (
                            <p className="text-[10px] text-slate-400">+{formatDuration(p.seconds_since_prev)}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LoginActivityCard;
